import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { useGetVoucherType } from 'src/features/voucherTypes/api/useGetVoucherType';
import DeleteVoucherTypeFormModal from './forms/DeleteVoucherTypeFormModal';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { VOUCHER_USAGE_BADGE_OPTIONS } from 'src/features/voucherTypes/utils/constants';
import UpdateVoucherTypeFormModal from 'src/features/voucherTypes/components/forms/UpdateVoucherTypeFormModal';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const VoucherTypeDashboard = () => {
    const { formatMessage, formatNumber } = useIntl();
    const navigate = useNavigate();
    const { voucherTypeId = '' } = useParams();

    const permissionManageVouchers = useHasPermissions([PERMISSION.MANAGE_VOUCHERS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(voucherTypesMessages.voucherTypeDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: voucherType } = useGetVoucherType({
        voucherTypeId,
    });

    const formatValidDuration = (value: string) => {
        const [days] = value.split(' ');
        return `P${Number(days)}D`;
    };

    const isActiveAsString = voucherType?.is_active ? 'true' : 'false';

    const dashboardBlockData = {
        section1: [
            {
                title: commonMessages.name,
                value: voucherType?.name ?? '-',
            },
            {
                title: voucherTypesMessages.code,
                value: voucherType?.code ?? '-',
            },
            {
                title: generalMessages.validFrom,
                value: getFormattedDateTimeForDetailPanel(voucherType?.valid_from) ?? '-',
            },
            {
                title: generalMessages.validTo,
                value: voucherType?.valid_to ? getFormattedDateTimeForDetailPanel(voucherType?.valid_to) : '-',
            },
            {
                title: voucherTypesMessages.isActive,
                value: (
                    <Badge theme={booleanBadgeOptions[isActiveAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isActiveAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isActiveAsString].label)}
                    </Badge>
                ),
            },
            {
                title: voucherTypesMessages.usage,
                value: voucherType ? (
                    <Badge theme={VOUCHER_USAGE_BADGE_OPTIONS[voucherType.usage].theme || 'gray'}>
                        <FontAwesomeIcon icon={VOUCHER_USAGE_BADGE_OPTIONS[voucherType.usage].icon as IconProp} />
                        {formatMessage(VOUCHER_USAGE_BADGE_OPTIONS[voucherType.usage].label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
        ],
        section2: [
            {
                title: commonMessages.currency,
                value: voucherType?.currency ? <CellCurrencyBadge currency={voucherType?.currency} /> : '-',
            },
            {
                title: pricingPoliciesMessages.amount,
                value:
                    voucherType?.amount && voucherType.currency
                        ? formatNumber(voucherType?.amount / 100, {
                              style: 'currency',
                              currency: voucherType.currency,
                          })
                        : '-',
            },
            {
                title: discountPoliciesMessages.percent,
                value: voucherType?.percent ? `${(Number(voucherType.percent) * 100).toFixed(2)}%` : '-',
            },
        ],
        section3: [
            {
                title: discountPoliciesMessages.validDuration,
                value: voucherType?.valid_duration ? formatValidDuration(voucherType?.valid_duration) : '-',
            },
            {
                title: voucherTypesMessages.maxCount,
                value: voucherType?.max_count ?? '-',
            },
        ],
        section4: [
            {
                title: generalMessages.description,
                value: voucherType?.description ?? '-',
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link" onClick={() => navigate('/voucher-types')}>
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(voucherTypesMessages.backToAllVoucherTypes)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {voucherType?.name}
                        </SectionBlockTitle>
                        {permissionManageVouchers && voucherType && (
                            <div className="flex flex-row gap-2">
                                <UpdateVoucherTypeFormModal
                                    semantics={{
                                        entryName: formatMessage(voucherTypesMessages.voucher).toLowerCase(),
                                    }}
                                    selectedVoucherType={voucherType}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.edit)}</span>
                                    </Button>
                                </UpdateVoucherTypeFormModal>
                                <DeleteVoucherTypeFormModal
                                    selectedVoucherType={voucherType}
                                    semantics={{
                                        entryName: formatMessage(voucherTypesMessages.voucher).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteVoucherTypeFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={3} />
                </SectionBlock>
            </div>
        </>
    );
};

export default VoucherTypeDashboard;
